import { RefObject, useCallback } from "react"
import { useRunAiFeature } from "@/hooks/useRunAiFeature"
import { chatCaseSummaryConfirmationMessageQuery } from "@/modules/chats/services/ai/chatCaseSummaryConfirmationMessageQuery"
import { useChatInputStateGetter } from "@/modules/chats/stores/useChatInputStore"
import { AiTrackingMetadata } from "@/modules/chats/types/chat-message"
import { ChatThread, getAutoTranslationTargetLanguage } from "@/modules/chats/types/chat-thread"

export type ChatCaseSummaryResult = {
  text: string
  aiFeatureId: string
  fileHash: string
  aiRequestDurationMs: number
  aiAnonymizationRequestDurationMs: number
}

export const buildChatCaseSummaryTrackingMetadata = (
  result: ChatCaseSummaryResult,
  previousText: string,
): AiTrackingMetadata => ({
  id: result.aiFeatureId,
  name: "Chat case summary confirmation message",
  previousText,
  newText: result.text,
  aiFeature: "CHAT_CASE_SUMMARY_CONFIRMATION_MESSAGE",
  fileHash: result.fileHash,
  aiRequestDurationMs: result.aiRequestDurationMs,
  aiAnonymizationRequestDurationMs: result.aiAnonymizationRequestDurationMs,
  createdAt: new Date().toISOString(),
})

export const useChatCaseSummary = (selectedThread: ChatThread, cancelledRef?: RefObject<boolean>) => {
  const getChatInputState = useChatInputStateGetter()
  const runAiFeature = useRunAiFeature()
  const targetAgentLanguage = getAutoTranslationTargetLanguage(selectedThread)

  return useCallback(async (): Promise<ChatCaseSummaryResult | null> => {
    let summaryResult: ChatCaseSummaryResult | null = null

    await runAiFeature({
      context: "CHAT",
      start: () => getChatInputState().setAiRunning("CHAT_CASE_SUMMARY_CONFIRMATION"),
      stop: () => getChatInputState().setAiRunning("NONE"),
      faro: {
        event: "aiFeature_chatCaseSummaryConfirmationMessage",
        feature: "Chat case summary confirmation message",
      },
      run: (aiFeatureId) =>
        chatCaseSummaryConfirmationMessageQuery(selectedThread.patient.country, {
          aiFeatureId,
          appointmentId: selectedThread.appointmentId,
          age: selectedThread.patient.age.years,
          gender: selectedThread.patient.gender,
          targetAgentLanguage,
        }),
      selectMeta: (response) => response.chatCaseSummaryConfirmationMessage,
      onSuccess: (meta, aiFeatureId) => {
        if (cancelledRef?.current) {
          return
        }
        if (!meta.text) {
          return
        }

        summaryResult = {
          text: meta.text,
          aiFeatureId,
          fileHash: meta.fileHash,
          aiRequestDurationMs: meta.aiRequestDurationMs,
          aiAnonymizationRequestDurationMs: meta.aiAnonymizationRequestDurationMs,
        }
      },
      onError: (error) => console.error("Chat Case Summary Confirmation Message request is failed", error),
    })

    return summaryResult
  }, [selectedThread, targetAgentLanguage, cancelledRef, getChatInputState, runAiFeature])
}
